import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import classnames, { Argument } from 'classnames/bind';
import { useBoolean, useDebounceFn, useInterval, useMemoizedFn } from 'ahooks';
import { useObserver } from 'mobx-react';
import { useIntl } from '@@/plugin-locale/localeExports';
import { handle, invoke } from '@/utils/Bridge';
import { AppProvider, AppShape, LOCALE } from '@/utils/appProvider';
import Style from './index.less';
import { setLocale } from '@/utils/locale';
import RecordList, { useRecordListData } from '@/components/RecordList';
import Icon from '@/components/Icon';
import { getRecordList, recordMsg } from '@/components/RecordList/listMock';
// import RecordLocaleSelect from '@/pages/PC/RecordPanel/RecordLocaleSelect';
import { Props as RecordLocaleSelectProps } from '@/pages/PC/RecordPanel/RecordLocaleSelect/container';
import { useInfoInit, useRecordStomp } from '@/pages/PC/RecordPanel/serverHooks';
import { stompMsgToRecordMsg } from '@/client/models/stomp';
import { compareVersions } from 'compare-versions';

// https://localhost:7788/meet/pc/recordpanel

// const $c = classnames.bind(Style);

// 平台类型常量
const PLATFORM = {
  WINDOWS: '0',
  MAC: '4'
} as const;

interface Props {
  $c: (...args: Argument[]) => string;
  RecordLocaleSelect: React.FC<RecordLocaleSelectProps>;
}

const PCRecordPanel: React.FC<Props> = ({ $c, RecordLocaleSelect }) => {
  const location = useLocation();
  const { formatMessage } = useIntl();
  const $i = (mid: string) => {
    return formatMessage({ id: mid });
  };
  const [ready, setReady] = useState(false);

  const context: AppShape = useMemo(() => {
    return {
      locale: LOCALE.CN,
      prefixCls: 'popo-meeting',
      getPrefixCls: ((prefix: string) => (suffix: string, prefixCls?: string) => {
        if (!prefixCls) {
          prefixCls = suffix ? `${prefix}-${suffix}` : prefix;
        }
        return prefixCls;
      })('popo-meeting'),
    };
  }, []);

  const queryObj = useMemo(() => {
    // 示例链接：http://10.219.156.201:7788/meet/pc/recordpanel?appVersion=3.53.2&deviceId=ed6a61f8ad21bd726f153a7289f6d65a&meetingId=11389993&deviceType=0&locale=zh-CN
    const search = new URLSearchParams(location.search);
    const result = ['appVersion', 'deviceId', 'meetingId', 'deviceType', 'locale'].reduce(
      (pre, cur) => {
        if (search.get(cur)) {
          pre[cur] = search.get(cur);
        } else {
          pre.ready = false;
        }
        return pre;
      },
      { ready: true } as Record<string, string | boolean>
    );
    result.locale && setLocale(result.locale);
    return result;
  }, [location.search]);

  // deviceType = 4 表示 mac 端
  const isMac = queryObj.deviceType === PLATFORM.MAC;

  // 判断是否开启 AI 转录
  const aiMinutes = useMemo(() => {
    const search = new URLSearchParams(location.search);
    for (const [key, value] of search.entries()) {
      if (key === 'aiMinutes' && value === '1') {
        return true;
      }
    }
    return false;
  }, [location.search]);

  // 判断是否需要更改更多按钮位置
  const shouldFixMoreButton = useMemo(() => {
    const { deviceType, appVersion } = queryObj;
    // 各平台最低版本要求
    const MIN_VERSION_REQUIREMENTS = {
      [PLATFORM.WINDOWS]: '4.37.5',
      [PLATFORM.MAC]: '4.25.5'
    };
    const minVersion = MIN_VERSION_REQUIREMENTS[deviceType];

    if (!minVersion || !appVersion) {
      return false;
    }
    return compareVersions(appVersion, minVersion) > 0;
  }, [queryObj.appVersion, queryObj.deviceType]);

  const [show, { toggle, setFalse }] = useBoolean(false);

  const { info, meetingInfo, qtReady, ableChangeLanguage } = useInfoInit();
  const { resMsg, connect } = useRecordStomp();

  useEffect(() => {
    meetingInfo && setReady(true);
  }, [meetingInfo]);

  const {
    listKey,
    recordList,
    hasMore,
    setHasMore,
    loadHistoryRecord,
    pushRecord,
    clearRecord,
    reduceList,
  } = useRecordListData();

  const fetchListIsLoading = useRef(false);
  const fetchList = useMemoizedFn(() => {
    if (fetchListIsLoading.current) return;
    fetchListIsLoading.current = true;
    return API.client.meetingSubtitleV3.queryMsgByTimestamp
      .request({
        channelId: info.meetingInfo?.channelId,
        timestamp: recordList?.[0]?.timestamp || new Date().getTime(),
        size: 50,
      })
      .then((res) => {
        if (res.status === 1 && res.data) {
          const { hasMore, data } = res.data;
          setHasMore(!!hasMore);
          // loadHistoryRecord(data?.sort((a, b) => a.timestamp - b.timestamp));
          loadHistoryRecord(
            data?.map((n) => {
              return {
                yxuid: n.speakerYxMeetingUid,
                ...n,
              };
            })
          );
        }
      })
      .finally(() => {
        fetchListIsLoading.current = false;
      });
  });

  // // mock数据加载
  // const fetchList = useCallback(() => {
  //   return getRecordList(50).then((ar) => {
  //     loadHistoryRecord(
  //       ar.list.map((n, index) => {
  //         return {
  //           ...n,
  //           name: '你好',
  //           timestamp: new Date().getTime() + 10 * index,
  //         };
  //       })
  //     );
  //   });
  // }, []);

  // // mock消息推送
  // useInterval(() => {
  //   const msg = recordMsg();
  //   msg.timestamp = new Date().getTime();
  //   pushRecord(msg);
  // }, 1000);

  const [isTranscripting, setIsTranscripting] = useState(true);
  // 是否为主持人
  const [isSelfHost, setIsSelfHost] = useState(true);

  useEffect(() => {
    handle('transcriptionChange', (data) => {
      if (typeof data?.isWork === 'boolean') {
        setIsTranscripting(data?.isWork);
      }
      if (typeof data?.isSelfHost === 'boolean') {
        setIsSelfHost(data?.isSelfHost);
      }
    });
  }, []);

  const { run: initList } = useDebounceFn(
    (clearAll: boolean) => {
      if (ready) {
        // clearRecord(clearAll);
        connect(isTranscripting);
      }
    },
    { wait: 100 }
  );
  useEffect(() => {
    initList(isTranscripting);
  }, [ready, isTranscripting]);

  useEffect(() => {
    initList(true);
  }, [info.languageSettingParams.language, info.languageSettingParams.translateTargetLanguage]);

  useEffect(() => {
    if (resMsg) {
      const msg = stompMsgToRecordMsg(resMsg);
      console.log('转录pushRecord', msg);
      pushRecord(msg);
    }
  }, [resMsg]);
  useEffect(() => {
    const closeSelect = () => setFalse();
    window.addEventListener('blur', closeSelect);
    document.documentElement.className = 'pp-pc';
    // 告之客户端端，页面加载成功
    invoke('webLoadSuccessed', {});
    return () => {
      window.removeEventListener('blur', closeSelect);
      document.documentElement.className = '';
    };
  }, []);

  const unReadyDom = useMemo(() => {
    if (!queryObj.ready) {
      return (
        <div className={$c('loading-box')}>
          <div>
            客户端参数不全
            <br />
            {JSON.stringify(queryObj)}
          </div>
        </div>
      );
    }
    if (!qtReady) {
      return (
        <div className={$c('reload-wrap')}>
          <div className={$c('reload-text')}>
            {$i('TRANSCRIPTION_ACTIVATION_FAILED')}{' '}
            <span className={$c('reload-btn')} onClick={() => window.history.go(0)}>
              {$i('TRY_AGAIN')}
            </span>
          </div>
        </div>
      );
    }
    if (!ready) {
      return (
        <div className={$c('loading-box')}>
          <Icon name="icon-loading_w_nor" className={$c('loading-icon')} />
        </div>
      );
    }
    return null;
  }, [ready, queryObj.ready, qtReady]);

  return useObserver(() => (
    <AppProvider value={context}>
      <div className={$c('pc-record-panel', { 'mac': isMac })}>
        {ableChangeLanguage && (
          <div className={$c('top-tools')}>
            <div className={$c('tool-btn', { 'tool-btn-top': shouldFixMoreButton })} onClick={() => toggle()}>
              <div className={$c('icon-wrap')}>
                <Icon className={$c('btn-icon')} name="icon-more" />
              </div>
            </div>
            {/* <div
              className={$c('tool-btn-1')}
              onClick={() => {
                setIsTranscripting(!isTranscripting);
              }}
            >
              测试按钮
            </div> */}
            <RecordLocaleSelect
              $c={$c}
              show={show}
              close={setFalse}
              className={$c('locale-modal')}
            />
          </div>
        )}

        {unReadyDom ? (
          <div className={$c('record-list')}>{unReadyDom}</div>
        ) : (
          <div className={$c('record-list', { 'has-bottom': !isTranscripting })}>
            <RecordList
              list={recordList}
              loadMore={fetchList}
              hasMore={hasMore}
              key={listKey}
              meetingInfo={info.meetingInfo}
            />
            {!isTranscripting && (
              <div className={classnames($c('bottom-title'), { hide: isTranscripting })}>
                {aiMinutes ? (isSelfHost ? $i('STOPPED_AI_MINUTES') : $i('STOPPED_AI_MINUTES_OTHER')) : $i('STOPPED_TRANSCRIPTION')}
              </div>
            )}
          </div>
        )}
      </div>
    </AppProvider>
  ));
};

export default PCRecordPanel;
